import { useCallback, useEffect, useState } from 'react';
import { useToast } from '../../hooks/toast';
import {
  EditOutlined,
  PlusCircleOutlined,
  RedoOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import {
  ATable,
  MenuOptions,
  StatusActive,
  DropdownOptions,
  PageIntro,
  PageLayout,
  PagePanel,
} from '../../components';
import { ITerminology } from '../../shared/interfaces';
import TerminologyCreateEdit from './CreateEdit';
import { getTerminologies } from '../../http';
import { useAuth } from '../../hooks/auth';
import { PermissionActions } from '../../shared/enums/permissions.enum';

const Terminologies: React.FC = () => {
  const { addToast } = useToast();
  const { permissionFind } = useAuth();
  const [search, setSearch] = useState<string>('');
  const [terminologies, setTerminologies] = useState<ITerminology[]>([]);
  const [terminology, setTerminology] = useState<ITerminology | null>(null);
  const [modalCreate, setModalCreate] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [limit, setLimit] = useState<number>(10);
  const [page, setPage] = useState<number>(1);
  const [pages, setPages] = useState<number>();

  const listTerminologies = useCallback(async () => {
    setLoading(true);
    setTerminologies([]);
    try {
      const response = await getTerminologies(
        limit,
        (page - 1) * limit,
        search,
      );
      setPages(response.count);
      setTerminologies(response.data);
    } catch (err) {
      addToast({
        type: 'error',
        title: 'Erro',
        description: 'Erro ao listar terminologias',
      });
    } finally {
      setLoading(false);
    }
  }, [limit, search, page, addToast]);

  const renderModalTerminologyCreateEdit = () => {
    if (modalCreate)
      return (
        <TerminologyCreateEdit
          open={modalCreate}
          close={() => setModalCreate(false)}
          terminology={terminology}
          setTerminologies={setTerminologies}
        />
      );
  };
  useEffect(() => {
    listTerminologies();
  }, [page, search, limit, listTerminologies]);

  return (
    <PageLayout>
      <PageIntro
        badge="Cadastros"
        icon={<UnorderedListOutlined />}
        title="Terminologias"
        description="Mantenha a base de terminologias organizada para uso clínico e administrativo com a mesma linguagem visual do sistema."
      />
      <MenuOptions
        menu_items={[
          {
            label: 'Atualizar',
            key: '1',
            icon: <RedoOutlined />,
            onClick: () => listTerminologies(),
          },
          permissionFind([PermissionActions.terminologiesCreate])
            ? {
                label: 'Cadastrar',
                key: '2',
                icon: <PlusCircleOutlined />,
                onClick: () => {
                  setTerminology(null);
                  setModalCreate(true);
                },
              }
            : null,
        ]}
        showInputSearch
        search={search}
        setSearch={setSearch}
      />
      <PagePanel>
        <ATable
          scroll={{ y: 510 }}
          columns={[
            {
              title: 'Id',
              width: 80,
              render: (value, record, index) => <>{index + 1}</>,
            },
            {
              title: 'Descrição',
              dataIndex: 'description',
            },
            {
              title: 'Status',
              dataIndex: 'status',
              width: 95,
              render: (value: boolean) => <StatusActive status={value} />,
            },
            {
              title: 'Opções',
              width: 130,
              render: (cell: ITerminology) => (
                <DropdownOptions
                  items={[
                    permissionFind([PermissionActions.terminologiesUpdate])
                      ? {
                          label: 'Editar',
                          key: '1',
                          icon: <EditOutlined />,
                          onClick: () => {
                            setTerminology(cell);
                            setModalCreate(true);
                          },
                        }
                      : null,
                  ]}
                />
              ),
            },
          ]}
          loading={loading}
          dataSource={terminologies}
          page={page}
          setPage={setPage}
          pages={pages}
          limit={limit}
          setLimit={setLimit}
        />
      </PagePanel>
      {renderModalTerminologyCreateEdit()}
    </PageLayout>
  );
};

export default Terminologies;
